import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useWeldLength } from "#modules/part/view/useWeldLength";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { PartialProcessStep } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStep";
import { useAssemblyAlert } from "#modules/quoteRequestLines/views/useAssemblyAlert";
import { getIndustrialProcessWording } from "#shared/helpers/getIndustrialProcessWording";
import { Icon } from "#shared/icons/Icon";
import { AIButton } from "#shared/view/components/AIButton";
import { Flex } from "#shared/view/components/Flex";
import { Paper } from "#shared/view/components/Paper";
import { Text } from "#shared/view/components/Text";
import { IndustrialProcessSchema, IndustrialProcessType } from "shared";

import { buildProcessStepOptionValue, PREFIX } from "./ProcessSteps/useProcessStepsSelectData";

const assemblyProcessSteps: IndustrialProcessType[] = [
  IndustrialProcessSchema.enum.ASSEMBLING,
  IndustrialProcessSchema.enum.CRIMPING,
  IndustrialProcessSchema.enum.PRESSING,
  IndustrialProcessSchema.enum.WELDING,
];

export const SuggestedAssemblyProcessSteps = ({
  allPredictedProcessSteps,
  addProcessStep,
  hasChildren,
  partId,
}: {
  allPredictedProcessSteps: PartialProcessStep[] | undefined;
  addProcessStep: (processStepOptionValue: string) => void;
  hasChildren: boolean;
  partId: string | undefined;
}) => {
  const { control } = useFormContext<QuoteRequestLineFormData>();

  const { t } = useTranslation();

  const processSteps = useWatch({ control, name: "processSteps" });
  const { weldLength } = useWeldLength(partId);

  const { shouldShowAssemblyAlert: shouldSuggestConsumableProcessSteps } = useAssemblyAlert({
    processSteps,
    hasChildren,
  });

  const availableAssemblingProcessSteps = allPredictedProcessSteps?.filter((processStep) =>
    assemblyProcessSteps.includes(processStep.industrialProcess),
  );

  const hasWeldingProcessStep = processSteps?.some(
    (processStep) => processStep.industrialProcess === IndustrialProcessSchema.enum.WELDING,
  );
  const hasWeldLength = weldLength !== null && weldLength > 0;
  const availableWeldingProcessSteps = allPredictedProcessSteps?.filter(
    (processStep) => processStep.industrialProcess === IndustrialProcessSchema.enum.WELDING,
  );
  const shouldSuggestWeldingProcessSteps =
    hasChildren &&
    hasWeldLength &&
    !hasWeldingProcessStep &&
    !!availableWeldingProcessSteps?.length;

  if (!shouldSuggestConsumableProcessSteps && shouldSuggestWeldingProcessSteps) {
    return (
      <Paper radius="sm" withBorder={true} p={"xs"} mt={"xs"}>
        <Flex align={"center"} gap={"xs"} mb={"xs"}>
          <Icon name="weld" size={20} />
          <Text fw={"bold"} fz={"xs"} c={"violet.5"}>
            {t("quoteRequestLine.weldLengthDetected")}
          </Text>
        </Flex>
        <Text fw={"bold"} mb={"xs"} ta={"center"} fz={"xs"}>
          {t("quoteRequestLine.suggestedWeldingProcessStep")}
        </Text>
        <Flex gap={"xs"} w={"100%"} justify={"space-between"} wrap={"wrap"}>
          {availableWeldingProcessSteps.map((processStep) => (
            <AIButton
              onClick={() => addProcessStep(buildProcessStepOptionValue(processStep, PREFIX))}
              key={processStep.description ?? processStep.industrialProcess}
              miw={"30%"}
              flex={1}
            >
              {availableWeldingProcessSteps.length > 1 && processStep.description
                ? processStep.description
                : getIndustrialProcessWording(processStep.industrialProcess)}
            </AIButton>
          ))}
        </Flex>
      </Paper>
    );
  }

  if (!shouldSuggestConsumableProcessSteps) {
    return null;
  }

  return (
    <Paper radius="sm" withBorder={true} p={"xs"} mt={"xs"}>
      <Flex align={"center"} gap={"xs"} mb={"xs"}>
        <Icon name="nut" size={20} />
        <Text fw={"bold"} fz={"xs"} c={"violet.5"}>
          {t("quoteRequestLine.consumableDetected")}
        </Text>
      </Flex>
      <Text fw={"bold"} mb={"xs"} ta={"center"} fz={"xs"}>
        {t("quoteRequestLine.suggestedAssemblyProcessSteps")}
      </Text>
      <Flex gap={"xs"} w={"100%"} justify={"space-between"} wrap={"wrap"}>
        {availableAssemblingProcessSteps?.map((processStep) => (
          <AIButton
            onClick={() => addProcessStep(buildProcessStepOptionValue(processStep, PREFIX))}
            key={processStep.industrialProcess}
            miw={"30%"}
            flex={1}
          >
            {getIndustrialProcessWording(processStep.industrialProcess)}
          </AIButton>
        ))}
      </Flex>
    </Paper>
  );
};
